import classNames from 'classnames';
import { useMemo, useState } from 'react';
// components
import Button from 'react-bootstrap/Button';
import Tab from 'react-bootstrap/Tab';
import Nav from 'react-bootstrap/Nav';
import CollapsedPanel from '../../../../components/CollapsedPanel';
import LazyTabPanel from '../../../../components/LazyTabPanel';
// API
import { useAuth } from '../../../../hooks/useAuth';
import { useEnv } from '../../../../context/env-context';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRotateRight,
  faCompressArrowsAlt,
  faExpandArrowsAlt,
} from '@fortawesome/free-solid-svg-icons';
// tabs
import NewFundingDetailsTab from './tabs/NewFundingDetailsTab';
import NewFundingRepaymentActivityTab from './tabs/NewFundingRepaymentActivityTab';
import NewFundingEventsLogTab from './tabs/NewFundingEventsLogTab';
import NewFundingPaymentLinksTab from './tabs/NewFundingPaymentLinksTab';
import NewFundingCustomRepaymentsTab from './tabs/NewFundingCustomRepaymentsTab';

const PANEL_TABS = {
  details: 'Details',
  repayment_activity: 'Repayment Activity',
  events_log: 'Events Log',
  payment_links: 'Payment Links',
  custom_repayments: 'Custom Repayments',
};

function NewFundingProfilePanel({
  collapsedMode,
  collapsedPanels,
  expandPanel,
  toggleProfilePanel,
  funding,
  consumer,
  spinner,
  setSpinner,
  refreshFundingData,
  setTotalExpectedBalance,
}) {
  const { user } = useAuth();
  const { audience } = useEnv();

  const role = useMemo(() => user[`${audience}/metadata`].role, [user, audience]);

  const [activeTab, setActiveTab] = useState('details');
  const [refreshTab, setRefreshTab] = useState('');

  const refresh = () => setRefreshTab(activeTab);

  const toggleActiveTab = tab => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  if (collapsedPanels.profile) {
    return <CollapsedPanel side="left" onClick={() => expandPanel(1)} />;
  }

  return (
    <div className="h-100 p-2 d-flex flex-column">
      <Tab.Container activeKey={activeTab}>
        <div className="d-flex justify-content-between">
          <Nav className="bo-common-nav border-bottom-0" variant="tabs">
            {Object.entries(PANEL_TABS)
              .filter(([key]) => {
                if (role !== 'Superuser' && key === 'payment_links') return false;
                if (funding.repaymentMode !== 'CUSTOM' && key === 'custom_repayments') return false;

                return true;
              })
              .map(([key, value]) => (
                <Nav.Item key={key}>
                  <Nav.Link
                    className={classNames('bo-common-nav-link', {
                      'bo-common-nav-link-active': activeTab === key,
                    })}
                    onClick={() => toggleActiveTab(key)}
                  >
                    {value}
                  </Nav.Link>
                </Nav.Item>
              ))}
          </Nav>
          <div className="d-flex">
            <Button
              className="bo-common-inline-button me-2 d-flex align-items-center"
              onClick={refresh}
            >
              <FontAwesomeIcon icon={faArrowRotateRight} size="1x" color="#5946A4" />
            </Button>
            {!collapsedMode && (
              <Button
                className="bo-common-inline-button me-2 d-flex align-items-center"
                onClick={toggleProfilePanel}
              >
                <FontAwesomeIcon
                  icon={collapsedPanels.details ? faCompressArrowsAlt : faExpandArrowsAlt}
                  size="1x"
                  color="#5946A4"
                />
              </Button>
            )}
          </div>
        </div>
        <Tab.Content className="bo-common-tabs">
          <LazyTabPanel activeTab={activeTab} tabId="details">
            <NewFundingDetailsTab
              fundingId={funding.id}
              fundingEvents={funding.events}
              fundingStatus={funding.status}
              consumerIsTest={consumer.isTest}
              refreshTab={refreshTab}
              setRefreshTab={setRefreshTab}
              spinner={spinner}
              setSpinner={setSpinner}
              refreshFundingData={refreshFundingData}
            />
          </LazyTabPanel>
          <LazyTabPanel activeTab={activeTab} tabId="repayment_activity">
            <NewFundingRepaymentActivityTab
              fundingId={funding.id}
              refreshTab={refreshTab}
              setRefreshTab={setRefreshTab}
            />
          </LazyTabPanel>
          <LazyTabPanel activeTab={activeTab} tabId="events_log">
            <NewFundingEventsLogTab
              fundingLog={funding.log}
              refreshTab={refreshTab}
              setRefreshTab={setRefreshTab}
              spinner={spinner}
              setSpinner={setSpinner}
              refreshFundingData={refreshFundingData}
            />
          </LazyTabPanel>
          <LazyTabPanel activeTab={activeTab} tabId="payment_links">
            <NewFundingPaymentLinksTab
              fundingId={funding.id}
              fundingPaymentLinks={funding.paymentLinks}
              fundingOutstandingAmount={funding.outstandingAmount}
              refreshTab={refreshTab}
              setRefreshTab={setRefreshTab}
              spinner={spinner}
              setSpinner={setSpinner}
              refreshFundingData={refreshFundingData}
            />
          </LazyTabPanel>
          <LazyTabPanel activeTab={activeTab} tabId="custom_repayments">
            <NewFundingCustomRepaymentsTab
              fundingFutureRepayments={funding.futureRepayments}
              fundingRepaymentMode={funding.repaymentMode}
              fundingId={funding.id}
              fundingCurrentAmount={funding.currentAmount}
              consumerId={consumer.id}
              refreshTab={refreshTab}
              setRefreshTab={setRefreshTab}
              setActiveTab={setActiveTab}
              setTotalExpectedBalance={setTotalExpectedBalance}
              spinner={spinner}
              setSpinner={setSpinner}
              refreshFundingData={refreshFundingData}
            />
          </LazyTabPanel>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}

export default NewFundingProfilePanel;
